import { Navigate, Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, ShieldCheck, Droplets, CheckCircle2, AlertTriangle, FlaskConical } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SchemaJsonLd, localBusinessSchema, serviceSchema, faqSchema, breadcrumbSchema } from "@/lib/seo";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import { GLOBAL_FAQS } from "@/lib/faqs";
import { useAuth } from "@/lib/auth";
import {
  getLocationBySlug,
  getRegion,
  locationUrl,
  packageMeta,
  isDraft,
  LOCATIONS,
} from "@/lib/locationContent";
import { canonical_suburb_path, REGIONS } from "@/data/locationTree";
import SupportedByPartnerTile from "@/components/SupportedByPartnerTile";

const BASE_URL = SEO_DEFAULTS.baseUrl;
const STAFF_ROLES = new Set(["admin", "lab_tech"]);

/**
 * Default hero image used by local pages that don't have a per-suburb
 * hero photo configured in locations.json. This is the Lake MacDonald
 * hero — a Sunshine Coast hinterland acreage home with a visible
 * rainwater tank and roof catchment — chosen as the on-brand fallback
 * because it's representative of the rural/tank customers our local
 * pages target. Per-suburb `hero_image.src` values in locations.json
 * always win over this fallback (see render block below).
 */
const DEFAULT_LOCAL_PAGE_HERO_IMAGE = {
  src: "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/uglk9lic_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2008_15_07%20PM.png",
  alt: "Sunshine Coast acreage home with rainwater tank and roof catchment — representative local water-testing imagery",
};

const AREA_TYPE_LABEL = {
  "coastal": "Coastal Residential",
  "hinterland": "Hinterland",
  "rural-acreage": "Rural / Acreage",
  "urban": "Urban / Mixed",
  "mixed": "Mixed Property Mix",
  "inland": "Inland",
};

export default function LocationLanding({ _slugOverride } = {}) {
  const { slug: slugFromParams } = useParams();
  const slug = _slugOverride || slugFromParams;
  const data = getLocationBySlug(slug);
  const { user } = useAuth();
  const canPreviewDrafts = !!user && STAFF_ROLES.has(user.role);

  // Always call hooks. Use safe defaults when data is missing so we don't
  // violate the rules-of-hooks; render the redirect after the hooks below.
  const draft = !data || isDraft(data);
  // Feb 2026 · National SEO Architecture — canonical URL now points to
  // the hierarchical path (`/locations/queensland/sunshine-coast/doonan`)
  // whenever the suburb belongs to a known state/region. Legacy flat
  // URLs (`/locations/doonan-water-testing`) still resolve, but they
  // point at the new canonical so Google consolidates equity.
  const canonicalPath = data ? canonical_suburb_path(data) : "/locations";
  const pageUrl = data ? `${BASE_URL}${canonicalPath}` : BASE_URL;
  // Jun 2026 SEO audit — unique ≤60-char title per suburb, brand included
  // (old per-suburb seo_title values were 80+ chars with a doubled brand).
  const seoTitle = data
    ? `${data.suburb_name} Water Testing | Aware Water Testing`
    : "Water Testing — Sunshine Coast";
  const seoDesc = data?.seo_description || "Local Sunshine Coast water testing.";
  useSEO({
    title: seoTitle,
    description: seoDesc,
    url: pageUrl,
    noindex: draft,
  });

  if (!data) return <Navigate to="/locations" replace />;
  // Public visitors must never see draft suburb pages, even via direct URL.
  // Staff (admin / lab_tech) get the preview so they can QA before publish.
  if (isDraft(data) && !canPreviewDrafts) {
    return <Navigate to="/locations" replace />;
  }

  const primary = packageMeta(data.recommended_primary_test);
  const secondary = packageMeta(data.recommended_secondary_test);
  // `featured_packages` (optional, per-location) lets data drive which booking
  // CTAs appear mid-page. Falls back to the recommended_primary / _secondary
  // pair so existing pages keep working unchanged.
  const featured = Array.isArray(data.featured_packages) && data.featured_packages.length > 0
    ? data.featured_packages.map(packageMeta).filter(Boolean)
    : [primary, secondary].filter(Boolean);
  const region = getRegion(data.region_cluster);
  const areaLabel = AREA_TYPE_LABEL[data.area_type] || "Local";

  const localFAQs = data.faq_items || [];
  const sharedFAQs = (GLOBAL_FAQS || []).slice(0, 2);
  const combinedFAQs = [...localFAQs, ...sharedFAQs];

  // Sep 2026 SEO audit — the breadcrumb used to jump Home › Locations ›
  // Suburb, pointing at `/locations#<region>` (a fragment), so the state
  // and region hubs received no inbound links from their own suburbs
  // (14 region hubs sat below 3 inbound links). Mirror the canonical URL
  // hierarchy instead: /locations › /locations/<state> ›
  // /locations/<state>/<region> › suburb. Legacy flat URLs keep the old
  // fragment behaviour.
  const segs = canonicalPath.split("/").filter(Boolean);
  const titleise = (s) =>
    String(s).split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  const hierarchy = segs.length === 4 && segs[0] === "locations"
    ? [
        { name: titleise(segs[1]), url: `${BASE_URL}/locations/${segs[1]}` },
        { name: region?.label || titleise(segs[2]), url: `${BASE_URL}/locations/${segs[1]}/${segs[2]}` },
      ]
    : region
      ? [{ name: region.label, url: `${BASE_URL}/locations#${region.key}` }]
      : [];

  const breadcrumbs = [
    { name: "Home", url: BASE_URL },
    { name: "Locations", url: `${BASE_URL}/locations` },
    ...hierarchy,
    { name: data.suburb_name, url: pageUrl },
  ];

  // Nearby suburbs may reference either a locations.json slug (legacy
  // pilot entries) or a plain suburb name (Jun 2026 flagship entries).
  // Matched, publicly-visible pages become canonical hierarchical links
  // so link equity flows between suburb pages; unmatched names render
  // as plain chips for local context.
  const nearby = (data.nearby_suburbs || []).map((raw) => {
    const key = String(raw).toLowerCase();
    const match = LOCATIONS.find(
      (l) => l.slug === raw || l.suburb_name.toLowerCase() === key,
    );
    // Same draft-visibility rule as /locations: drafts are only linked
    // for staff. Public visitors never see a link pointing at a hidden
    // page (those links would 404-redirect to /locations anyway).
    const linkable = match && (canPreviewDrafts || !isDraft(match));
    return {
      name: match ? match.suburb_name : String(raw),
      to: linkable ? canonical_suburb_path(match) : null,
    };
  });

  // Jun 2026 SEO audit — deterministic same-region ring links so every
  // suburb page RECEIVES inbound links from sibling pages (fixes
  // orphan / weakly-linked findings). Each page links to the next 4
  // siblings alphabetically (wrap-around), so every sibling is in turn
  // linked from 4 predecessors — without dumping every suburb link
  // onto every page.
  const regionDef = Object.values(REGIONS).find((r) =>
    r.clusters.some((c) => c.key === data.region_cluster),
  );
  const clusterKeys = new Set((regionDef ? regionDef.clusters : []).map((c) => c.key));
  const siblings = LOCATIONS
    .filter((l) =>
      l.slug !== data.slug &&
      clusterKeys.has(l.region_cluster) &&
      (canPreviewDrafts || !isDraft(l)))
    .sort((a, b) => a.suburb_name.localeCompare(b.suburb_name));
  const ringLinks = [];
  if (siblings.length > 0) {
    const idx = siblings.filter((l) => l.suburb_name.localeCompare(data.suburb_name) < 0).length;
    for (let k = 0; k < Math.min(4, siblings.length); k++) {
      const l = siblings[(idx + k) % siblings.length];
      ringLinks.push({ name: l.suburb_name, to: canonical_suburb_path(l) });
    }
  }
  // Merge curated nearby entries with ring links, dedupe by name
  // (prefer a linked entry over an unlinked chip), cap at 8 so the
  // block never becomes a link dump.
  const mergedNearby = new Map();
  [...nearby, ...ringLinks].forEach((n) => {
    const key = n.name.toLowerCase();
    const prev = mergedNearby.get(key);
    if (!prev || (!prev.to && n.to)) mergedNearby.set(key, n);
  });
  const nearbyAreas = Array.from(mergedNearby.values()).slice(0, 8);

  return (
    <div className="min-h-screen bg-white" data-testid="location-landing">
      <Header />

      {draft && (
        <div
          data-testid="draft-banner"
          className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm py-2 px-4 text-center font-medium"
        >
          PREVIEW · DRAFT — this page is not published and is excluded from search engines.
        </div>
      )}

      <SchemaJsonLd id={`jsonld-localbusiness-${data.slug}`} data={localBusinessSchema()} />
      <SchemaJsonLd
        id={`jsonld-service-${data.slug}`}
        data={serviceSchema(
          `Water Testing — ${data.suburb_name}`,
          data.seo_description,
          data.suburb_name,
        )}
      />
      <SchemaJsonLd id={`jsonld-faq-${data.slug}`} data={faqSchema(combinedFAQs.map((f) => ({ q: f.q, a: f.a })))} />
      <SchemaJsonLd id={`jsonld-breadcrumb-${data.slug}`} data={breadcrumbSchema(breadcrumbs)} />

      {/* Hero — supports an optional suburb-specific background photo via
          data.hero_image.src. When no per-suburb image is configured, the
          shared DEFAULT_LOCAL_PAGE_HERO_IMAGE (Lake MacDonald acreage hero)
          fills the gap so every local page renders with on-brand imagery. */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white overflow-hidden">
        {(() => {
          const heroImg = (data.hero_image && data.hero_image.src)
            ? data.hero_image
            : DEFAULT_LOCAL_PAGE_HERO_IMAGE;
          const isFallback = !(data.hero_image && data.hero_image.src);
          return (
            <>
              <img
                src={heroImg.src}
                alt={heroImg.alt || `Water testing context image for ${data.suburb_name}`}
                data-testid="location-hero-image"
                data-hero-source={isFallback ? "default-fallback" : "suburb-specific"}
                loading="eager"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-slate-900/70 to-cyan-900/75" />
            </>
          );
        })()}
        <div className="relative max-w-6xl mx-auto px-6 py-16 sm:py-24">
          <nav className="text-xs text-cyan-200 mb-4" aria-label="Breadcrumb">
            {breadcrumbs.map((b, i) => {
              const isLast = i === breadcrumbs.length - 1;
              const to = b.url.replace(BASE_URL, "") || "/";
              return (
                <span key={b.url}>
                  {i > 0 && <span className="mx-2 text-cyan-400">/</span>}
                  {isLast ? (
                    <span className="text-white">{b.name}</span>
                  ) : (
                    <Link to={to} className="hover:text-white" data-testid={`suburb-crumb-${i}`}>
                      {b.name}
                    </Link>
                  )}
                </span>
              );
            })}
          </nav>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="secondary" className="bg-cyan-100 text-cyan-900 mb-4">
              <MapPin className="w-3 h-3 mr-1" /> {areaLabel} · Postcode {data.postcode}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
              Water Testing in {data.suburb_name}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-cyan-50 max-w-3xl leading-relaxed">
              {data.hero_subtitle ||
                `Local rainwater tank and bore water testing for ${data.suburb_name} — plain-English reports, drop-off at our Noosaville lab.`}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book-water-test" data-testid="hero-book-btn">
                <Button size="lg" className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold">
                  Book a Water Test <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/#water-tests" data-testid="hero-packages-btn">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 border-white/40 text-white hover:bg-white/20"
                >
                  View Test Packages
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Local intro */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
            Why water testing matters in {data.suburb_name}
          </h2>
          <p className="mt-6 text-slate-700 leading-relaxed text-[15px] sm:text-base">
            {data.local_intro}
          </p>
          {data.local_risk_summary && (
            <div className="mt-8 p-5 rounded-xl bg-amber-50 border border-amber-200 flex gap-4">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900 leading-relaxed">
                <strong>Local context:</strong> {data.local_risk_summary}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Feb 2026 · Supported-by tile — renders only when the suburb's
          region has a confirmed active partner. Silently hides
          otherwise (no placeholder attribution). */}
      <SupportedByPartnerTile regionCluster={data.region_cluster} suburbName={data.suburb_name} />

      {/* Likely water sources + common risks */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Droplets className="w-5 h-5 text-cyan-700" /> Likely water sources in {data.suburb_name}
            </h2>
            <ul className="mt-5 space-y-3" data-testid="water-sources">
              {(data.likely_water_sources || []).map((src, i) => (
                <li key={i} className="flex gap-3 items-start text-slate-700 text-[15px]">
                  <CheckCircle2 className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-1" />
                  <span>{src}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-700" /> Common local water concerns
            </h2>
            <ul className="mt-5 space-y-3" data-testid="water-risks">
              {(data.common_water_risks || []).map((risk, i) => (
                <li key={i} className="flex gap-3 items-start text-slate-700 text-[15px]">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-1" />
                  <span>{risk}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Recommended packages */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
            Recommended test packages for {data.suburb_name}
          </h2>
          <p className="mt-3 text-slate-600 text-[15px]">
            Based on the typical water-supply profile and property mix in this suburb. You can also
            mix and match samples — e.g. one tap and one tank — on a single booking.
          </p>
          <div className="mt-8 grid md:grid-cols-2 gap-6" data-testid="recommended-packages">
            {featured.map((pkg, idx) => (
              <PackageCard
                key={pkg.label}
                pkg={pkg}
                primary={idx === 0}
                slug={data.suburb_name}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Sample drop-off */}
      <section className="py-12 bg-slate-50 border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-cyan-700" /> Sample collection &amp; drop-off
          </h2>
          <ul className="mt-5 space-y-2 text-slate-700 text-[15px]" data-testid="dropoff-checklist">
            <li className="flex gap-2 items-start">
              <CheckCircle2 className="w-4 h-4 text-cyan-700 mt-1 flex-shrink-0" />
              <span>Use a sterile sample bottle (we supply one when you book).</span>
            </li>
            <li className="flex gap-2 items-start">
              <CheckCircle2 className="w-4 h-4 text-cyan-700 mt-1 flex-shrink-0" />
              <span>Keep your sample cool and out of direct sunlight after collection.</span>
            </li>
            <li className="flex gap-2 items-start">
              <CheckCircle2 className="w-4 h-4 text-cyan-700 mt-1 flex-shrink-0" />
              <span>Bacterial samples must reach the lab within 24 hours of collection.</span>
            </li>
            <li className="flex gap-2 items-start">
              <CheckCircle2 className="w-4 h-4 text-cyan-700 mt-1 flex-shrink-0" />
              <span>Drop-off: <strong>1/37 Gateway Drive, Noosaville QLD 4566</strong> — Monday to Thursday before 2 pm.</span>
            </li>
          </ul>
          <p className="mt-4 text-[14px] text-slate-600">{data.sample_dropoff_note}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white" data-testid="local-faq">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
            Local FAQ — {data.suburb_name}
          </h2>
          <div className="mt-8 space-y-6">
            {combinedFAQs.map((faq, i) => (
              <div key={i} className="border-b border-slate-200 pb-5">
                <h3 className="font-semibold text-slate-900 text-[16px]">{faq.q}</h3>
                <p className="mt-2 text-slate-700 text-[14.5px] leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-lg font-bold text-slate-900">Useful pages</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="internal-links">
            <InternalLink to="/" label="Main water testing page" />
            <InternalLink to="/book-water-test?package=basic" label="AWARE 4-Point — $129" />
            <InternalLink to="/book-water-test?package=tank" label="AWARE Tank 360 — $209" />
            <InternalLink to="/book-water-test?package=bore" label="AWARE Bore 360 — $229" />
            <InternalLink to="/passport" label="AWARE Water Health Record" />
            <InternalLink to="/knowledge-hub" label="Knowledge Hub articles" />
          </div>
          {nearbyAreas.length > 0 && (
            <div className="mt-8">
              <h3 className="text-sm font-semibold text-slate-600">Nearby areas we test</h3>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="nearby-suburbs">
                {nearbyAreas.map((n) =>
                  n.to ? (
                    <Link
                      key={n.name}
                      to={n.to}
                      data-testid={`nearby-suburb-link-${n.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="px-3 py-1.5 rounded-full bg-white border border-slate-300 text-slate-700 text-[13px] hover:border-cyan-500 hover:text-cyan-800"
                    >
                      {n.name} →
                    </Link>
                  ) : (
                    <span
                      key={n.name}
                      data-testid={`nearby-suburb-chip-${n.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-500 text-[13px]"
                    >
                      {n.name}
                    </span>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function PackageCard({ pkg, primary, slug }) {
  return (
    <div
      data-testid={primary ? "primary-package-card" : "secondary-package-card"}
      className={
        "rounded-2xl p-6 border " +
        (primary
          ? "bg-cyan-50 border-cyan-300 ring-1 ring-cyan-200"
          : "bg-white border-slate-200")
      }
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <FlaskConical className={"w-5 h-5 " + (primary ? "text-cyan-700" : "text-slate-500")} />
          <h3 className="font-bold text-slate-900 text-[17px]">{pkg.label}</h3>
        </div>
        {primary && (
          <Badge className="bg-cyan-700 text-white text-[11px]">Recommended for {slug}</Badge>
        )}
      </div>
      <p className="mt-3 text-slate-700 text-[14.5px]">{pkg.short_pitch}</p>
      <div className="mt-5 flex items-center justify-between">
        <span className="text-2xl font-bold text-slate-900">{pkg.price_label}</span>
        <Link to={pkg.book_path}>
          <Button
            size="sm"
            className={
              primary
                ? "bg-cyan-700 hover:bg-cyan-800 text-white"
                : "bg-slate-900 hover:bg-slate-800 text-white"
            }
          >
            Book this test <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

function InternalLink({ to, label }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between rounded-lg bg-white border border-slate-200 px-4 py-3 text-slate-700 text-[14.5px] hover:border-cyan-500 hover:text-cyan-800"
    >
      <span>{label}</span>
      <ArrowRight className="w-4 h-4" />
    </Link>
  );
}
